// Pairing flow: the QR encodes /app/#pair=<base64url JSON>. The payload stays
// in the URL fragment, so it never reaches the portal server. We mint a fresh
// phone keypair for this daemon, prove the one-time secret over the E2E
// handshake, and persist the entry on success.
import { el, clear } from "../dom.js";
import { b64urlDecode } from "../crypto.js";
import { Connection } from "../relay.js";
import { saveDaemon, newPhoneKey } from "../store.js";
import type { DaemonEntry } from "../store.js";

interface PairPayload {
  v: number;
  portal: string;
  daemon: string;
  dpk: string;
  secret: string;
  exp: number;
  name: string;
}

export function parsePairFragment(hash: string): PairPayload | null {
  const m = hash.match(/#pair=([A-Za-z0-9_-]+)/);
  if (!m) return null;
  try {
    const json = new TextDecoder().decode(b64urlDecode(m[1]));
    const p = JSON.parse(json) as PairPayload;
    if (p.v !== 1 || !p.portal || !p.daemon || !p.dpk || !p.secret) return null;
    return p;
  } catch {
    return null;
  }
}

export function renderPair(root: HTMLElement, payload: PairPayload, onPaired: (daemonId: string) => void): void {
  clear(root);

  if (payload.exp && payload.exp * 1000 < Date.now()) {
    root.append(
      header(),
      el("div", { class: "error-banner" }, [
        "This pairing code has expired. Generate a new QR from Fabrika's settings and scan again.",
      ]),
    );
    return;
  }

  const nameInput = el("input", {
    type: "text",
    value: defaultDeviceName(),
    autocomplete: "off",
  }) as HTMLInputElement;
  const err = el("div", {});
  const pairBtn = el("button", { class: "primary" }, ["Pair this phone"]) as HTMLButtonElement;

  pairBtn.onclick = async () => {
    pairBtn.disabled = true;
    err.textContent = "";
    err.className = "";
    const entry: DaemonEntry = {
      daemonId: payload.daemon,
      portal: payload.portal,
      daemonPub: payload.dpk,
      myPriv: newPhoneKey(),
      name: payload.name || "Fabrika project",
    };
    try {
      const conn = new Connection(entry, {}, { secret: payload.secret, deviceName: nameInput.value.trim() || "Phone" });
      const hello = await conn.connect();
      conn.close();
      if (hello.project) entry.name = hello.project;
      saveDaemon(entry);
      history.replaceState(null, "", "#"); // drop the secret from the URL
      onPaired(entry.daemonId);
    } catch (e) {
      pairBtn.disabled = false;
      err.className = "error-banner";
      err.textContent = (e as Error).message;
    }
  };

  root.append(
    header(),
    el("div", { class: "card" }, [
      el("h3", {}, [`Pair with “${payload.name || payload.daemon.slice(0, 12)}”`]),
      el("p", { class: "meta" }, [`via ${payload.portal}`]),
      el("p", {}, [
        "Pairing is end-to-end encrypted: the relay server only ever sees ciphertext. " +
          "This code works once.",
      ]),
      el("div", { class: "field" }, [el("p", { class: "meta" }, ["Device name (shown in Fabrika settings)"]), nameInput]),
      err,
      el("div", { class: "actions" }, [pairBtn]),
    ]),
  );
}

function header(): HTMLElement {
  return el("header", { class: "topbar" }, [el("h1", {}, ["Fabrika"])]);
}

function defaultDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  return "Phone";
}
